import api from './api';
import type { Workspace } from '../types';

/**
 * Management of the ACTIVE workspace (the tenant). Gated on MANAGE_ORG_SETTINGS
 * (owner/admin) in the backend; the workspace is resolved from the request
 * context, never passed in the body.
 */
export const workspaceApi = {
  rename: async (name: string): Promise<Workspace> => {
    const { data } = await api.patch('/api/workspace', { name });
    return data.workspace as Workspace;
  },

  /**
   * Create a NEW workspace owned by the caller (identity-level, reachable by
   * orphans). Returns the new workspace and its default org — switch into
   * `organization.id` to make it the active context.
   */
  create: async (
    name: string,
  ): Promise<{ workspace: Workspace; organization: { id: string; name: string } }> => {
    const { data } = await api.post('/api/workspace', { name });
    return data as { workspace: Workspace; organization: { id: string; name: string } };
  },

  /**
   * Close the entire workspace (owner-only, staged). Cascades — the backend
   * soft-deletes every business and source under the workspace in one shot, cuts
   * all member access, and cancels billing. `password` is re-verified server-side
   * unless the owner is OAuth-only (no password set).
   */
  close: async (password?: string): Promise<{ message: string }> => {
    const { data } = await api.post('/api/workspace/close', { password });
    return data as { message: string };
  },

  /**
   * "Delete my account" — leave the active workspace, and (if it was the caller's
   * last) permanently delete the identity. Owners are refused (transfer/close
   * first). `password` is re-verified unless the caller is OAuth-only.
   */
  deleteAccount: async (
    password?: string,
  ): Promise<{ message: string; identityErased: boolean; remainingWorkspaces: number }> => {
    const { data } = await api.post('/api/workspace/leave', { password });
    return data as { message: string; identityErased: boolean; remainingWorkspaces: number };
  },
};
